"use client";

import { useEffect, useState } from "react";
import { apiBaseUrl } from "../utils/api";
import {
  Copy,
  CreditCard,
  Calendar,
  IndianRupee,
  Gift,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function GiftCardDetailsPage() {
  const [orders, setOrders] = useState([]);
  const [uniqueId, setUniqueId] = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [collapsedCards, setCollapsedCards] = useState(new Set());
  const [ordersLoaded, setOrdersLoaded] = useState(false);

  // Hardcoded uniqueId

  useEffect(() => {
    if (!uniqueId) {
      setLoading(false);
      setError("Please log in to view your gift card details.");
      return;
    }

    const fetchGiftCardOrders = async () => {
      try {
        console.log("unique id", uniqueId);
        setLoading(true);
        setError(null);
        const response = await fetch(
          `${apiBaseUrl}/api/user/gc/giftCardOrders/${uniqueId}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ message: "Unknown error" }));
          throw new Error(
            errorData.error || response.statusText || "Failed to fetch data"
          );
        }

        const data = await response.json();
        // Sort orders by date (most recent first)
        const sortedOrders = (data || []).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setOrders(sortedOrders);

        // Set all cards as collapsed by default
        if (sortedOrders.length > 0 && !ordersLoaded) {
          const allOrderIds = new Set(
            sortedOrders.map((order) => order.orderId)
          );
          setCollapsedCards(allOrderIds);
          setOrdersLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch gift card orders:", err);
        setError("Failed to load gift card details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchGiftCardOrders();
  }, [uniqueId]);

  const showToast = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const copyToClipboard = (text, label) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      showToast(`${label} copied to clipboard!`, "success");
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      showToast(`${label} copied to clipboard!`, "success");
    }
  };

  const toggleCardCollapse = (orderId) => {
    setCollapsedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = (status, transactionStatus) => {
    if (status === "completed" || transactionStatus === "success") {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (status === "pending" || transactionStatus === "pending") {
      return <Clock className="h-5 w-5 text-yellow-500" />;
    } else {
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusColor = (status, transactionStatus) => {
    if (status === "completed" || transactionStatus === "success") {
      return "bg-green-100 text-green-800";
    } else if (status === "pending" || transactionStatus === "pending") {
      return "bg-yellow-100 text-yellow-800";
    } else {
      return "bg-red-100 text-red-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">Loading your gift cards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-lg text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-blue-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <Gift className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-700 font-medium">
            No gift cards found
          </p>
          <p className="text-gray-500 mt-2">
            Your gift card purchases will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-6 px-4 relative">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">My Gift Cards</h1>
        </div>

        {toastMessage && (
          <div
            className={`fixed top-4 right-4 p-3 rounded-lg shadow-lg text-white z-50 transition-all duration-300 ${
              toastType === "success" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            <div className="flex items-center gap-1 text-sm">
              <CheckCircle className="h-4 w-4" />
              {toastMessage}
            </div>
          </div>
        )}

        <div className="grid gap-4 max-w-4xl mx-auto">
          {orders.map((order) => (
            <div
              key={order.orderId}
              className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
            >
              {/* Order Header */}
              <div className="bg-indigo-600 p-4 text-white">
                <div
                  className="flex items-center justify-between flex-wrap gap-2 cursor-pointer"
                  onClick={() => toggleCardCollapse(order.orderId)}
                >
                  <div>
                    <h2 className="text-lg font-bold">{order.brandName}</h2>
                    <div className="flex items-center gap-2 text-indigo-200 text-sm">
                      <span className="text-sm">Order #{order.orderId}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-xl font-bold">
                        <IndianRupee className="h-5 w-5" />
                        {formatCurrency(order.amount).replace("₹", "")}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-indigo-200">
                        <Calendar className="h-4 w-4" />
                        {formatDate(order.date)}
                      </div>
                    </div>
                    <div className="ml-2">
                      {collapsedCards.has(order.orderId) ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronUp className="h-5 w-5" />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!collapsedCards.has(order.orderId) && (
                <>
                  {/* Status and Rewards */}
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(order.status, order.transactionStatus)}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            order.status,
                            order.transactionStatus
                          )}`}
                        >
                          {order.transactionStatus || order.status}
                        </span>
                      </div>
                      <div className="flex gap-3 text-xs">
                        {order.gold_reward && (
                          <div className="flex items-center gap-1 text-yellow-600 font-medium">
                            <Gift className="h-4 w-4" />
                            <span>Gold: {order.gold_reward}</span>
                          </div>
                        )}
                        {order.cashback_amount && (
                          <div className="flex items-center gap-1 text-green-600 font-medium">
                            <IndianRupee className="h-4 w-4" />
                            <span>
                              Cashback: {formatCurrency(order.cashback_amount)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Gift Cards */}
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Gift Cards ({order.cards.length})
                    </h3>
                    <div className="grid gap-3">
                      {order.cards.map((card, cardIndex) => (
                        <div
                          key={`${order.orderId}-${card.sku}-${cardIndex}`}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-800 text-sm">
                                {card.productName}
                              </h4>
                            </div>
                            {card.validity && (
                              <div className="text-right">
                                <p className="text-xs text-gray-500">
                                  Valid Until
                                </p>
                                <p className="font-semibold text-gray-800 text-sm">
                                  {formatDate(card.validity)}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="grid md:grid-cols-2 gap-3">
                            {/* Card Number */}
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-medium text-gray-600">
                                  Card Number
                                </label>
                                {card.cardNumber && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      copyToClipboard(
                                        card.cardNumber,
                                        "Card Number"
                                      )
                                    }
                                    className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-indigo-600 transition-colors"
                                    aria-label="Copy card number"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                              <p className="font-mono text-sm font-semibold text-gray-800 break-all">
                                {card.cardNumber || "Not Available"}
                              </p>
                            </div>

                            {/* Card PIN */}
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-medium text-gray-600">
                                  PIN
                                </label>
                                {card.cardPin && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      copyToClipboard(card.cardPin, "Card PIN")
                                    }
                                    className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-indigo-600 transition-colors"
                                    aria-label="Copy card PIN"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                              <p className="font-mono text-sm font-semibold text-gray-800">
                                {card.cardPin || "Not Available"}
                              </p>
                            </div>
                          </div>

                          {card.issuanceDate && (
                            <div className="mt-3 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-500">
                                Issued on {formatDate(card.issuanceDate)}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
